/**
 * Plan 23 — Service worker registration + force-update lifecycle.
 *
 * The vite-plugin-pwa `injectRegister: 'auto'` setting already drops a
 * minimal `navigator.serviceWorker.register('/sw.js')` call into the
 * HTML bundle, so the SW installs automatically on first page load.
 * This module layers Cafeteria-specific behavior on top:
 *
 *   1. **Force-update reload handler.** When the SW posts a
 *      `{ type: 'FORCE_UPDATE_RELOAD' }` message (after SCHOOL_ADMIN
 *      triggers a force-update via Plan 25), this handler shows a
 *      brief "Updating..." overlay and reloads after 3 seconds so the
 *      user sees the transition.
 *
 *   2. **Periodic update check.** Every 10 minutes during idle, the
 *      registration's `update()` method is called so a new SW
 *      published by a deploy installs in the background without the
 *      user having to refresh.
 *
 *   3. **Manual force-update API.** `requestForceUpdate()` posts the
 *      `FORCE_UPDATE` message to the active SW — used by the
 *      DevicesPage's "Force update" button (Plan 25).
 *
 *   4. **Update-available announcement.** The SW activates new builds
 *      immediately (`skipWaiting` + `clientsClaim` in sw.ts), which
 *      keeps navigations fresh but leaves already-OPEN tabs running
 *      the pre-deploy bundle indefinitely — nothing reloads a live
 *      page. When a new SW takes over (`controllerchange` on a page
 *      that already HAD a controller), this module marks the update
 *      and dispatches `SW_UPDATED_EVENT`; the app shell listens and
 *      shows a persistent "Refresh" toast (`useSwUpdateToast`). While
 *      the user is ACTIVE, the toast is the only nudge — this module
 *      never reloads under someone's hands.
 *
 *   5. **Away-tab auto-reload.** The toast solved nothing for users
 *      who never tap it: a phone PWA or tablet left open ran stale
 *      code for days. When an update is ready AND the tab has been
 *      HIDDEN for ≥ SW_AUTO_RELOAD_MIN_HIDDEN_MS (user away — app
 *      backgrounded, tablet idle on another screen), the page reloads
 *      itself: while still hidden when timers run (invisible to the
 *      user), or at the moment of return otherwise. The 5-minute floor
 *      keeps brief app-switches (incl. Android's print UI, which hides
 *      the tab) from dropping in-progress work; a user mid-task who
 *      switches away for hours had lost that state to the OS anyway.
 *
 * Safe in test / SSR environments: every entry point checks for
 * `navigator.serviceWorker` and bails out cleanly when unavailable.
 */

export const FORCE_UPDATE_RELOAD_DELAY_MS = 3_000
export const SW_UPDATE_CHECK_INTERVAL_MS = 10 * 60 * 1000
/** How long a tab must be continuously hidden before an update-ready page may auto-reload. */
export const SW_AUTO_RELOAD_MIN_HIDDEN_MS = 5 * 60 * 1000

/** Window event dispatched when a NEW service worker has taken over an
 *  already-controlled page (i.e., a deploy landed under this open tab). */
export const SW_UPDATED_EVENT = 'ordo:sw-updated'

let lifecycleInstalled = false
let swUpdateReady = false
let forceReloadPending = false

/** Whether a new SW took over this page earlier in its lifetime — lets a
 *  late-mounting listener (the app shell renders after auth gates) catch
 *  up on an announcement it missed. */
export function isSwUpdateReady(): boolean {
  return swUpdateReady
}

/**
 * Pure decision: should a controllerchange be announced as an update?
 *
 *   - `hadController` false → this is the SW's FIRST claim of the page
 *     (fresh install under `clientsClaim`), not a deploy — skip.
 *   - `forceReloadPending` → the admin force-update flow is about to
 *     reload this page anyway (its skipWaiting fires controllerchange
 *     too) — skip rather than flash a toast under the reload overlay.
 */
export function resolveSwUpdateNotification(args: {
  hadController: boolean
  forceReloadPending: boolean
}): 'announce' | 'skip' {
  if (!args.hadController || args.forceReloadPending) {
    return 'skip'
  }
  return 'announce'
}

/**
 * Pure decision: may an update-ready page reload itself right now?
 * True only when an update is ready, no force-update reload is already
 * in flight, and the tab has been continuously hidden for at least
 * `minHiddenMs` (`hiddenAtMs` null = the tab is/was visible — never
 * auto-reload under an active user; the toast covers them).
 */
export function shouldAutoReloadHiddenTab(args: {
  updateReady: boolean
  forceReloadPending: boolean
  hiddenAtMs: number | null
  nowMs: number
  minHiddenMs?: number
}): boolean {
  const minHiddenMs = args.minHiddenMs ?? SW_AUTO_RELOAD_MIN_HIDDEN_MS
  if (!args.updateReady || args.forceReloadPending || args.hiddenAtMs === null) {
    return false
  }
  return args.nowMs - args.hiddenAtMs >= minHiddenMs
}

/**
 * Install the page-side service-worker lifecycle behaviors. Idempotent
 * — calling more than once is a no-op. Called once from `main.tsx`
 * after React mounts.
 */
export function installServiceWorkerLifecycle(): void {
  if (lifecycleInstalled) {
    return
  }
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }
  lifecycleInstalled = true

  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = (event.data ?? null) as { type?: string } | null
    if (!data || data.type !== 'FORCE_UPDATE_RELOAD') {
      return
    }
    // Suppress the update toast for the takeover this reload implies. The
    // FORCE_UPDATE skipWaiting can fire controllerchange BEFORE this
    // message lands, so a toast may still flash for up to the reload
    // delay — cosmetic: the fullscreen overlay covers it, then we reload.
    forceReloadPending = true
    showForceUpdateOverlay()
    window.setTimeout(() => {
      window.location.reload()
    }, FORCE_UPDATE_RELOAD_DELAY_MS)
  })

  // Away-tab auto-reload (doc item 5). Tracks how long the tab has been
  // continuously hidden; when an update is ready and the away floor is
  // met, reloads — from the timer while still hidden (invisible), or at
  // the moment of return when the timer was throttled/frozen.
  let hiddenAtMs: number | null = document.visibilityState === 'hidden' ? Date.now() : null
  let hiddenReloadTimer: number | null = null

  function attemptHiddenReload(): void {
    if (shouldAutoReloadHiddenTab({ updateReady: swUpdateReady, forceReloadPending, hiddenAtMs, nowMs: Date.now() })) {
      forceReloadPending = true
      window.location.reload()
    }
  }

  function scheduleHiddenReload(): void {
    if (hiddenReloadTimer !== null || !swUpdateReady || hiddenAtMs === null) {
      return
    }
    const delay = Math.max(0, hiddenAtMs + SW_AUTO_RELOAD_MIN_HIDDEN_MS - Date.now())
    hiddenReloadTimer = window.setTimeout(attemptHiddenReload, delay)
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAtMs = Date.now()
      scheduleHiddenReload()
      return
    }
    // Returning to the tab: if the away floor was met while the timer was
    // throttled or the page frozen, reload NOW — before the user resumes
    // on stale code. Otherwise reset the away clock.
    attemptHiddenReload()
    hiddenAtMs = null
    if (hiddenReloadTimer !== null) {
      window.clearTimeout(hiddenReloadTimer)
      hiddenReloadTimer = null
    }
  })

  // Deploy-under-an-open-tab detection (doc item 4). `hadController`
  // distinguishes a real update takeover from the SW's first-ever claim
  // of this page; announce on EVERY qualifying takeover (a second deploy
  // in one session re-signals — the toast id dedupes rendering).
  let hadController = Boolean(navigator.serviceWorker.controller)
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const decision = resolveSwUpdateNotification({ hadController, forceReloadPending })
    hadController = true
    if (decision === 'skip') {
      return
    }
    swUpdateReady = true
    window.dispatchEvent(new CustomEvent(SW_UPDATED_EVENT))
    // Deploy landed while the tab was already backgrounded (the common
    // idle-tablet / pocketed-phone case) — arm the away reload.
    scheduleHiddenReload()
  })

  // getRegistration() rejects in some privacy modes; nothing to do about it.
  startPeriodicUpdateCheck().catch(() => {})
}

/**
 * Begin a 10-minute polling loop that asks the registered SW for an
 * update. The SW's lifecycle handles the actual swap; the loop just
 * pokes the registration so a kiosk picks up a fresh deploy without
 * waiting for the browser's default 24h cadence.
 *
 * The loop bails out when no registration is found (the SW hasn't
 * installed yet) — it'll be restarted on the next page load.
 */
async function startPeriodicUpdateCheck(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) {
    return
  }
  window.setInterval(() => {
    // Swallowed: an update probe that loses the network is expected and
    // self-heals on the next tick. Unhandled, it became a reported exception.
    registration.update().catch(() => {})
  }, SW_UPDATE_CHECK_INTERVAL_MS)
}

/**
 * Render a fullscreen "Updating..." overlay before the page reloads.
 * Idempotent — calling more than once during the same reload window
 * is a no-op.
 */
function showForceUpdateOverlay(): void {
  if (typeof document === 'undefined') {
    return
  }
  if (document.getElementById('ordo-force-update-overlay')) {
    return
  }
  const overlay = document.createElement('div')
  overlay.id = 'ordo-force-update-overlay'
  overlay.style.position = 'fixed'
  overlay.style.inset = '0'
  overlay.style.zIndex = '99999'
  overlay.style.display = 'flex'
  overlay.style.flexDirection = 'column'
  overlay.style.alignItems = 'center'
  overlay.style.justifyContent = 'center'
  overlay.style.background = 'rgba(15, 23, 42, 0.95)'
  overlay.style.color = '#f8fafc'
  overlay.style.fontFamily = 'system-ui, -apple-system, sans-serif'
  overlay.innerHTML =
    '<div style="font-size: 1.25rem; font-weight: 600;">Updating Ordo</div>' +
    '<div style="font-size: 0.875rem; margin-top: 0.5rem; opacity: 0.75;">Loading fresh code, one moment...</div>'
  document.body.appendChild(overlay)
}

/**
 * Trigger a force-update on the active SW. The Devices page calls
 * this when an admin clicks the per-kiosk button; the SW responds by
 * skipping waiting + asking every client to reload (which this page
 * receives via the message handler above).
 *
 * Returns `true` when the message was sent. Returns `false` when no
 * SW controller is available (the SW is still installing, or service
 * workers are disabled).
 */
export async function requestForceUpdate(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return false
  }
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) {
    return false
  }
  // Make sure the registration is up-to-date before forcing the swap;
  // otherwise we're just "skipping waiting" on the current SW which
  // does nothing.
  await registration.update()
  const target = registration.waiting ?? registration.installing ?? navigator.serviceWorker.controller
  if (!target) {
    return false
  }
  target.postMessage({ type: 'FORCE_UPDATE' })
  return true
}
